// Hex minimap — renders a MinimapSnapshot on a single <canvas> in the same pointy-top hex
// layout as the 3D board, with zoom, Fit Board, a hover tooltip and keyboard access.
//
// Module layout (design: docs/minimap-design.md): ./minimap/{minimapGeometry, minimapViewport,
// minimapPaint, MinimapCanvas, MinimapToolbar, MinimapTooltip, MinimapLegend,
// useMinimapInteraction}. This file stays the public export; props are backwards compatible.

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { cn } from './lib/utils.js';
import { MinimapCanvas } from './minimap/MinimapCanvas.js';
import { MinimapLayersPopover } from './minimap/MinimapLayersPopover.js';
import { MinimapLegend } from './minimap/MinimapLegend.js';
import { MinimapToolbar } from './minimap/MinimapToolbar.js';
import { MinimapTooltip } from './minimap/MinimapTooltip.js';
import { prepareBoard, tileAt } from './minimap/minimapBoard.js';
import type { Point } from './minimap/minimapGeometry.js';
import { tileKey, unitToAxial } from './minimap/minimapGeometry.js';
import { fmt, mergeLabels } from './minimap/minimapLabels.js';
import { type MinimapPrefs, loadPrefs, savePrefs } from './minimap/minimapPrefs.js';
import { describeTile, tooltipLines } from './minimap/minimapTooltipText.js';
import {
  boardEquals,
  colorsEqual,
  detailsEqual,
  markersEqual,
  minimapTileEquals,
  pingsEqual,
  pointEqual,
  polygonEqual,
  useStable,
} from './minimap/stable.js';
import type {
  MinimapDetails,
  MinimapLabels,
  MinimapMarker,
  MinimapPing,
  MinimapSnapshot,
  MinimapTile,
  TileVisibility,
} from './minimap/types.js';
import { useMinimapInteraction } from './minimap/useMinimapInteraction.js';
import { useReducedMotion } from './minimap/useReducedMotion.js';

export type {
  MinimapCityInfo,
  MinimapDetails,
  MinimapLabels,
  MinimapMarker,
  MinimapPing,
  MinimapSnapshot,
  MinimapTile,
  MinimapUnitInfo,
  TileVisibility,
} from './minimap/types.js';
export { PING_MS as minimapPingMs } from './minimap/minimapPaint.js';
export { tileKey } from './minimap/minimapGeometry.js';
export type { Point as MinimapPoint } from './minimap/minimapGeometry.js';

export interface MinimapProps {
  readonly snapshot: MinimapSnapshot;
  /** Optional overlay glyphs, drawn above the unit/city icons. */
  readonly markers?: readonly MinimapMarker[];
  /** Map player index → CSS hex colour (e.g. "#ff4444"). */
  readonly playerColors?: ReadonlyMap<number, string>;
  /** Called with (q, r) when a non-fog tile is clicked (or Enter on the keyboard focus hex). */
  readonly onTileClick?: (q: number, r: number) => void;
  /**
   * Primary-button drags move the 3D camera: called with the unit-space points under the
   * pointer at drag start and now (the App keeps the grabbed point under the cursor).
   */
  readonly onCameraDrag?: (from: Point, to: Point) => void;
  /**
   * A camera drag ended (pointer up or cancel). The host drops the grab so the
   * next drag starts a fresh one.
   */
  readonly onCameraDragEnd?: () => void;
  /** The 3D camera's ground footprint in unit space, stroked as the view box. */
  readonly cameraPolygon?: readonly Point[] | null;
  /** Draw that view box (default true). The host persists the choice. */
  readonly showCameraBox?: boolean;
  /** When set, the topbar gets a toggle for the view box. */
  readonly onToggleCameraBox?: () => void;
  /**
   * The ground point the 3D camera is centred on (the orbit target), in unit
   * space. Reported by the live region as "camera at q,r".
   */
  readonly cameraFocus?: Point | null;
  /** Transient event/click ping rings (M3). The App owns the clock and ageing. */
  readonly pings?: readonly MinimapPing[];
  /** False = static rings (reduced motion or the particle-effects setting off). */
  readonly animatePings?: boolean;
  readonly className?: string;
  /** City names / tiers / capitals and unit owners, keyed by `tileKey(q, r)`. */
  readonly details?: MinimapDetails;
  /** The local player's index (tooltip says "You"). */
  readonly localPlayer?: number;
  /** Translated strings (defaults to English). */
  readonly labels?: Partial<MinimapLabels>;
}

export { minimapTileEquals };
export type { MinimapLayers, MinimapPrefs, MinimapSize } from './minimap/minimapPrefs.js';
export { DEFAULT_PREFS as DEFAULT_MINIMAP_PREFS } from './minimap/minimapPrefs.js';

export function Minimap(props: MinimapProps) {
  if (props.snapshot.tiles.length === 0) return null;
  return <MinimapView {...props} />;
}

const SIZE_H: Record<MinimapPrefs['size'], string> = { s: '10rem', m: '15rem', l: '20rem' };

/** How long the tap chip stays up on a phone before it fades (spec §6). */
const TOUCH_CHIP_MS = 2600;

function MinimapView({
  snapshot,
  markers: markersProp,
  playerColors: colorsProp,
  onTileClick,
  onCameraDrag,
  onCameraDragEnd,
  cameraPolygon: cameraPolygonProp,
  showCameraBox,
  onToggleCameraBox,
  cameraFocus: cameraFocusProp,
  pings: pingsProp,
  animatePings,
  className,
  details: detailsProp,
  localPlayer,
  labels: labelOverrides,
}: MinimapProps) {
  const labels = useMemo(() => mergeLabels(labelOverrides), [labelOverrides]);
  // The sim republishes everything each tick; keep references stable while nothing visible
  // changed so the canvas repaints only on a real change.
  const board = useStable(
    useMemo(() => prepareBoard(snapshot), [snapshot]),
    boardEquals,
  );
  const markers = useStable(markersProp, markersEqual);
  const playerColors = useStable(colorsProp, colorsEqual);
  const details = useStable(detailsProp, detailsEqual);
  const reducedMotion = useReducedMotion();
  const cameraPolygon = useStable(cameraPolygonProp, polygonEqual);
  const cameraFocus = useStable(cameraFocusProp, pointEqual);
  const pings = useStable(pingsProp, pingsEqual);
  // The local player's own cities, for the `C` key (spec §5). Derived from the
  // enriched details, so it is stable while the board is.
  const ownCities = useMemo(() => {
    if (!details?.cities || localPlayer === undefined) return [];
    const out: { q: number; r: number }[] = [];
    for (const t of board.tiles) {
      if (!t.hasCity || t.visibility === 'fog') continue;
      const city = details.cities.get(tileKey(t.q, t.r));
      if (!city || city.neutral) continue;
      if (city.ownerId !== localPlayer) continue;
      out.push({ q: t.q, r: t.r });
    }
    return out;
  }, [details, localPlayer, board]);
  const ix = useMinimapInteraction({
    board,
    onTileClick,
    onCameraDrag,
    onCameraDragEnd,
    ownCities,
  });
  const [legendOpen, setLegendOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const legendId = useId();

  // Per-user view prefs (M4): layers, size, collapsed. Loaded once; every
  // change persists best-effort. `localStorage` is unavailable in some
  // embedded contexts — loadPrefs tolerates that.
  const [prefs, setPrefs] = useState<MinimapPrefs>(() =>
    loadPrefs(typeof localStorage === 'undefined' ? null : localStorage),
  );
  const updatePrefs = useCallback((patch: Partial<MinimapPrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      savePrefs(typeof localStorage === 'undefined' ? null : localStorage, next);
      return next;
    });
  }, []);
  const collapsed = prefs.collapsed;

  const tipCtx = useMemo(() => ({ labels, details, localPlayer }), [labels, details, localPlayer]);
  const hoverTile = ix.hover ? tileAt(board, ix.hover.q, ix.hover.r) : undefined;
  const hoverLines = ix.hover ? tooltipLines(hoverTile, ix.hover.q, ix.hover.r, tipCtx) : null;
  // On a phone there is no hover, so a tap shows the tile info as a chip. The chip
  // is transient (spec §6: "tap shows the tooltip as a toast chip") — it fades on
  // its own so it cannot sit on top of the map forever. The key names the tile, so
  // tapping a second tile restarts the timer instead of keeping the first one.
  const touchChipKey = ix.hover?.touch ? `${ix.hover.q},${ix.hover.r}` : '';
  const clearHover = ix.clearHover;
  useEffect(() => {
    if (!touchChipKey) return;
    const id = setTimeout(() => clearHover(), TOUCH_CHIP_MS);
    return () => clearTimeout(id);
  }, [touchChipKey, clearHover]);
  // A marker's label (e.g. "Champion (dragon)") joins the tooltip; never on fog.
  if (hoverLines && ix.hover && hoverTile && hoverTile.visibility !== 'fog') {
    for (const m of markers ?? []) {
      if (m.q === ix.hover.q && m.r === ix.hover.r && m.label) hoverLines.push(m.label);
    }
  }

  const cityCount = useMemo(
    () => board.tiles.reduce((n, t) => (t.hasCity && t.visibility !== 'fog' ? n + 1 : n), 0),
    [board],
  );
  const focusTile = ix.focus ? tileAt(board, ix.focus.q, ix.focus.r) : undefined;
  const zoomText = `${ix.zoom.toFixed(ix.zoom < 10 ? 1 : 0)}×`;
  const collapsedSummary = zoomText;
  // Where the 3D camera looks, in axial coordinates (spec §7 wants the live
  // region to say "camera at 12,4"). The camera's focus — the ground point the
  // orbit target sits on — is the position to report; the footprint polygon's
  // centre is NOT (a tilted camera's footprint is a long wedge, so its centre
  // sits well beyond the point the camera is actually over). The polygon
  // centroid is only a fallback for a host that publishes no focus.
  const cameraAt = useMemo(() => {
    if (cameraFocus) return unitToAxial(cameraFocus.x, cameraFocus.y);
    if (!cameraPolygon || cameraPolygon.length === 0) return null;
    let x = 0;
    let y = 0;
    for (const p of cameraPolygon) {
      x += p.x;
      y += p.y;
    }
    const { q, r } = unitToAxial(x / cameraPolygon.length, y / cameraPolygon.length);
    return { q, r };
  }, [cameraPolygon, cameraFocus]);
  const summary = fmt(labels.summary, {
    cities: cityCount,
    zoom: ix.zoom.toFixed(1),
    q: ix.focus?.q ?? '–',
    r: ix.focus?.r ?? '–',
    what: ix.focus ? describeTile(focusTile, ix.focus.q, ix.focus.r, tipCtx) : '',
    camera: cameraAt ? `${cameraAt.q},${cameraAt.r}` : '–',
  });
  const clickable = Boolean(
    onTileClick && hoverTile && hoverTile.visibility !== ('fog' satisfies TileVisibility),
  );

  return (
    // biome-ignore lint/a11y/useSemanticElements: the root keeps its div layout; role gives the minimap a landmark
    <div
      role="region"
      aria-label={labels.title}
      data-testid="minimap"
      data-appdom-id="minimap"
      data-appdom-type="Chart"
      data-appdom-scope="aside"
      data-appdom-label={labels.title}
      style={{ '--minimap-h': SIZE_H[prefs.size] } as React.CSSProperties}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-lg border border-border/70 bg-card/88 p-2 backdrop-blur-sm',
        // Height is mutually exclusive by state, so no two height utilities ever
        // compete in the cascade. Desktop: the size preset. Phone (below md): the
        // drawer height from spec §6, capped so it clears the HUD toggle bar.
        collapsed ? 'h-auto' : 'h-(--minimap-h,15rem) max-md:h-[min(58vh,24rem)]',
        className,
      )}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && (legendOpen || layersOpen)) {
          setLegendOpen(false);
          setLayersOpen(false);
        }
      }}
    >
      <div className="flex shrink-0 items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {labels.title}
          {collapsed && (
            <span className="ml-2 font-normal normal-case tracking-normal">{collapsedSummary}</span>
          )}
        </h3>
        {!collapsed && (
          <MinimapToolbar
            labels={labels}
            onZoomIn={ix.zoomIn}
            onZoomOut={ix.zoomOut}
            onFit={ix.fit}
            onToggleLegend={() => setLegendOpen((o) => !o)}
            onToggleLayers={() => setLayersOpen((o) => !o)}
            layersOpen={layersOpen}
            showCameraBox={showCameraBox}
            onToggleCameraBox={onToggleCameraBox}
            onCollapse={() => updatePrefs({ collapsed: true })}
            legendOpen={legendOpen}
            legendId={legendId}
            zoomText={zoomText}
            className="max-md:absolute max-md:right-3 max-md:top-1 max-md:z-10 max-md:mt-0"
          />
        )}
        {collapsed && (
          <button
            type="button"
            aria-label={labels.expand}
            title={labels.expand}
            onClick={() => updatePrefs({ collapsed: false })}
            data-testid="minimap-expand"
            className="mr-0.5 cursor-pointer rounded p-1 text-muted-foreground hover:text-foreground"
          >
            <svg
              viewBox="0 0 16 16"
              className="size-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 2v4H2M14 6h-4V2M10 14v-4h4M2 10h4v4" />
            </svg>
          </button>
        )}
      </div>

      {!collapsed && (
        <div ref={ix.containerRef} className="relative min-h-0 flex-1 overflow-hidden rounded">
          <MinimapCanvas
            canvasRef={ix.canvasRef}
            board={board}
            viewport={ix.viewport}
            size={ix.size}
            playerColors={playerColors}
            markers={markers}
            details={details}
            cameraPolygon={showCameraBox === false ? null : cameraPolygon}
            pings={pings}
            animatePings={animatePings}
            layers={prefs.layers}
            hover={ix.hover}
            focus={ix.focus}
            focused={ix.focused}
            reducedMotion={reducedMotion}
            tabIndex={0}
            role="application"
            aria-label={labels.ariaLabel}
            aria-describedby={`${legendId}-live`}
            data-testid="minimap-canvas"
            data-appdom-id="minimap-canvas"
            data-appdom-type="Chart"
            data-appdom-scope="aside"
            data-appdom-label={labels.title}
            className={cn(
              'absolute inset-0 block rounded outline-none focus-visible:ring-2 focus-visible:ring-ring',
              clickable ? 'cursor-pointer' : 'cursor-default',
            )}
            {...ix.handlers}
          />
          {hoverLines && ix.hover && (
            <MinimapTooltip
              lines={hoverLines}
              x={ix.hover.x}
              y={ix.hover.y}
              size={ix.size}
              touch={ix.hover.touch}
            />
          )}
          {layersOpen && (
            <MinimapLayersPopover
              id={`${legendId}-layers`}
              labels={labels}
              layers={prefs.layers}
              size={prefs.size}
              onToggleLayer={(layer) =>
                updatePrefs({ layers: { ...prefs.layers, [layer]: !prefs.layers[layer] } })
              }
              onSetSize={(size) => updatePrefs({ size })}
              onClose={() => setLayersOpen(false)}
            />
          )}
        </div>
      )}

      <p
        id={`${legendId}-live`}
        aria-live="polite"
        data-testid="minimap-live"
        data-appdom-id="minimap-live"
        data-appdom-type="Section"
        data-appdom-scope="aside"
        data-appdom-label={labels.title}
        className="sr-only"
      >
        {summary}
      </p>

      {legendOpen && <MinimapLegend id={legendId} labels={labels} />}
    </div>
  );
}
